export type AuthenticatedUser = {
  id: string;
  email: string;
  role: 'creator' | 'admin';
  createdAt: number;
};

type ApiErrorPayload = {
  success: false;
  error: {
    code: string;
    message: string;
    fields?: Record<string, string>;
  };
};

type UserPayload = {
  success: true;
  data: { user: AuthenticatedUser };
};

export class ApiRequestError extends Error {
  readonly code: string;
  readonly status: number;
  readonly fields?: Record<string, string>;

  constructor(status: number, payload?: ApiErrorPayload['error']) {
    super(payload?.message ?? 'Sunucuyla iletişim kurulamadı. Lütfen tekrar deneyin.');
    this.name = 'ApiRequestError';
    this.code = payload?.code ?? 'REQUEST_FAILED';
    this.status = status;
    this.fields = payload?.fields;
  }
}

const commonOptions = {
  credentials: 'same-origin' as const,
  headers: { Accept: 'application/json' },
};

async function parseResponse<T>(response: Response): Promise<T> {
  if (response.ok) {
    if (response.status === 204) {
      return undefined as T;
    }
    return (await response.json()) as T;
  }

  let payload: ApiErrorPayload | undefined;
  if (response.headers.get('content-type')?.includes('application/json')) {
    payload = (await response.json()) as ApiErrorPayload;
  }
  throw new ApiRequestError(response.status, payload?.error);
}

async function submitCredentials(
  action: 'login' | 'register',
  email: string,
  password: string,
): Promise<AuthenticatedUser> {
  const response = await fetch(`/odevlendir/api/auth/${action}`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email, password }),
  });
  const payload = await parseResponse<UserPayload>(response);
  return payload.data.user;
}

export async function getCurrentUser(): Promise<AuthenticatedUser | null> {
  const response = await fetch('/odevlendir/api/auth/me', commonOptions);
  if (response.status === 401) {
    return null;
  }
  const payload = await parseResponse<UserPayload>(response);
  return payload.data.user;
}

export function register(email: string, password: string): Promise<AuthenticatedUser> {
  return submitCredentials('register', email, password);
}

export function login(email: string, password: string): Promise<AuthenticatedUser> {
  return submitCredentials('login', email, password);
}

export async function logout(): Promise<void> {
  const response = await fetch('/odevlendir/api/auth/logout', {
    method: 'POST',
    ...commonOptions,
  });
  await parseResponse<void>(response);
}
